import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormControl, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { makeStyles } from '@mui/styles';
import { body1, body2, captionOverline, title1 } from '../../../styles/typography';
import { SuggestionIconType } from '../../../models/suggestionStatus.interface';
import tokens from '../../../styles/designTokens';
import {
  ReportBackgroundData, setSuggestionStatus, SuggestionStatusEnum, Citations,
  fetchJurisdictions, Jurisdiction,
} from '../../../redux/suggestions';
import {
  selectSuggestions,
  selectJurisdictions
} from '../../../selectors/suggestions';
import { HighlightsAccordion } from './highlights-accordion';
import ChatIcon from '../../../assets/svgs/chat.svg';
import { WordApiService } from '../../../services/word/api';
import { AppDispatch } from '../../../store';
const wordApi = new WordApiService();

const useStyles = makeStyles(() => ({
  resultsPage: {
    color: tokens.core3,
    backgroundColor: tokens.white
  },
  divider: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
    marginTop: '50%',
    alignSelf: 'stretch',
  },
  resultsPageBody: {
    flexGrow: 0.5,
    padding: '20px 20px 0 20px',
  },
  resultsPageHiddenItem: {
    display: 'none'
  },
  body1,
  info: {
    ...body1,
    color: tokens.core1
  },
  propertyName: {
    ...body2,
    color: tokens.core3
  },
  highlightsTitle: {
    ...title1,
    color: tokens.product100,
    padding: '0 0.25rem'
  },
  highlightsText: {
    ...body1,
    paddingTop: '1rem',
    color: tokens.core2
  },
  highlightsHeaderContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTop: `1px dotted ${tokens.neutral90}`,
    paddingTop: '1rem',
    marginBottom: '0.5rem'
  },
  highlightsHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    color: tokens.product100
  },
  countBadge: {
    ...captionOverline,
    backgroundColor: tokens.product15,
    color: tokens.product90,
    height: '1.625rem',
    width: '1.625rem',
    padding: 0,
    borderRadius: '144px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  }
}));

interface ReviewTabProps {
  reportBackgroundData: ReportBackgroundData;
}

enum SuggestionStatusAll { AllItems = 'allItems' }
type SuggestionStatusFilter = SuggestionStatusEnum | SuggestionStatusAll;

const ReviewTab: React.FC<ReviewTabProps> = ({ reportBackgroundData }) => {
  const classes = useStyles();
  const [statusFilter, setStatusFilter] = React.useState<SuggestionStatusFilter>(SuggestionStatusAll.AllItems);
  const [filteredSuggestions, setFilteredSuggestions] = React.useState([]);
  const dispatch = useDispatch<AppDispatch>();
  const jurisdictions = useSelector(selectJurisdictions);

  const jurisdictionMap = useMemo(() => {
    const map: { [key: string]: Jurisdiction } = {};
    jurisdictions.forEach(jurisdiction => {
      map[jurisdiction.id] = jurisdiction;
    });
    return map;
  }, [jurisdictions]);

  useEffect(() => {
    if (jurisdictions.length === 0) {
      dispatch(fetchJurisdictions());
    }
  }, [jurisdictions, dispatch]);

  const suggestions: {
    citations: Citations[]; promptIdentifier?: string; message: string, status?: SuggestionStatusEnum
  }[] = useSelector(selectSuggestions).flat();

  const suggestionsWithDeduplicatedCitations = suggestions.map((suggestion) => {
    const uniqueCitations = Array.from(new Set(suggestion.citations));
    return { ...suggestion, citations: uniqueCitations };
  });

  React.useEffect(() => {
    // Checks if the suggestion message contains the "Recommendation:" label surrounded by 0 to 2 asterisks to account for markdown formatting
    const validSuggestions = suggestionsWithDeduplicatedCitations.filter((suggestion) => suggestion?.message && /\*{0,2}Recommendation:\*{0,2}/i.test(suggestion.message));

    // todo: ER-376: If status are to be set to "default status" ('toReview') then the following line will be modified to:
    // const filteredSuggestions = validSuggestions.filter((sug) => statusFilter === SuggestionStatusAll.AllItems || sug.status === statusFilter);
    const suggestionsByFilter = validSuggestions.filter((sug) => (statusFilter === SuggestionStatusAll.AllItems || sug.status === statusFilter || (!sug.status && statusFilter === SuggestionStatusEnum.ToReview)));
    setFilteredSuggestions(suggestionsByFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(suggestions), statusFilter]);

  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  const [visitedIds, setVisitedIds] = React.useState<Set<string>>(new Set());

  const handleAccordionHeaderClick = (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
    }
    setVisitedIds(new Set([...Array.from(visitedIds), id]));
  };

  const filterByStatus = (event: SelectChangeEvent<SuggestionStatusFilter>) => {
    setStatusFilter(event.target.value as SuggestionStatusFilter);
  };

  const onStatusChange = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, id: string, status: SuggestionIconType) => {
    e.stopPropagation();
    dispatch(setSuggestionStatus({ id, status }));
  };

  const handleCitationsClick = async ({ recommendationIndex, citationIndex }: { recommendationIndex: number; citationIndex: number }) => {
    if (citationIndex >= 0) {
      const suggestion = filteredSuggestions[recommendationIndex];
      const citation = suggestion.citations[citationIndex] as Citations;


      await wordApi.withContext(async () => {
        return await wordApi.navigateToText(citation.text);
      });
    }
  };

  return (
    <>
      <div className={classes.resultsPage}>
        <Grid size={12} className={`${classes.resultsPageBody} ${classes.resultsPageHiddenItem}`}>
          <Typography className={classes.propertyName}>Reporting Entity</Typography>
          <Typography className={classes.info}>Name of Report Entity can be very long.</Typography>
        </Grid>

        <Grid size={12} className={classes.resultsPageBody}>
          <Typography className={classes.propertyName}>Jurisdiction</Typography>
          <Typography className={classes.info}>{jurisdictionMap[reportBackgroundData.jurisdiction]?.name}</Typography>
        </Grid>

        <Grid size={12} className={classes.resultsPageBody}>
          <Typography className={classes.propertyName}>Tax Year</Typography>
          <Typography className={classes.info}>{reportBackgroundData.taxYear}</Typography>
        </Grid>

        <Grid size={12} className={classes.resultsPageBody}>
          <div className={classes.highlightsHeaderContainer}>
            <div className={classes.highlightsHeaderLeft}>
              <ChatIcon />
              <Typography className={classes.highlightsTitle}>Highlights</Typography>
              <div className={classes.countBadge}>{filteredSuggestions.length}</div>
            </div>
            <FormControl>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={statusFilter}
                onChange={filterByStatus}
              >
                <MenuItem value={SuggestionStatusAll.AllItems}>All items</MenuItem>
                <MenuItem value={SuggestionStatusEnum.ToReview}>To review</MenuItem>
                <MenuItem value={SuggestionStatusEnum.Ignored}>Ignored</MenuItem>
                <MenuItem value={SuggestionStatusEnum.Completed}>Completed</MenuItem>
              </Select>
            </FormControl>
          </div>
          <Typography className={classes.highlightsText}>
            Review the comments and analyze what you need to change in the document.
          </Typography>

          <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '12px' }}>

            {filteredSuggestions.map((suggestion, idx) => {
              const recommendationDefaultValue = suggestion?.message?.substring(suggestion?.message?.indexOf('**Recommendation:**'));
              const id = suggestion.promptIdentifier || `${idx}`;
              return (
                <HighlightsAccordion
                  total={suggestion.citations.length || 0}
                  index={idx}
                  key={id}
                  id={id}
                  message={suggestion?.message}
                  recommendation={suggestion?.recommendation ?? recommendationDefaultValue}
                  status={suggestion?.status || SuggestionStatusEnum.ToReview}
                  isExpanded={expandedId === id}
                  isVisited={visitedIds.has(id)}
                  onHeaderClick={handleAccordionHeaderClick}
                  onStatusChange={onStatusChange}
                  handleCitationsClick={handleCitationsClick}
                />

              );
            })}

          </div>
        </Grid>
      </div>
    </>
  );
};

export default ReviewTab;